import { cx } from 'emotion';
import React, { useState, useMemo } from 'react';
import css from '../shared/css';

type Param = Readonly<Record<string, string>>;

interface Props {
  layerSource: string;
  setLayerSource: Function;
}

export const DEFAULT_WMS_PARAMS: Param[] = [
  { service: 'WMS' },
  { request: 'GetMap' },
  { version: '1.1.1' },
  { format: 'image/png' },
  { srs: 'EPSG:3857' },
  { crs: 'EPSG:3857' },
  { width: '256' },
  { height: '256' },
  { bgcolor: '0xFFFFFF' },
  { transparent: 'true' },
];

const WMSLayer: React.FC<Props> = ({ layerSource = '', setLayerSource }: Props) => {
  // Sets up editing information if it exists
  const baseUrl = useMemo(() => getBaseUrl(layerSource), [layerSource]);
  const urlParams = useMemo(() => getParamsArr(layerSource), [layerSource]);
  const urlLayers = useMemo(() => getLayers(urlParams), [urlParams]);

  const [base, setBase] = useState<string>(baseUrl);
  const [layers, setLayers] = useState<string>(urlLayers);
  const [params, setParams] = useState<Param[]>(removeLayers(urlParams));

  const handleUpdateBase = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newBase = e.target.value;
    let newParams = params;
    let newLayers = layers;

    if (hasParams(e.target.value)) {
      newBase = getBaseUrl(e.target.value);
      const allParams = getParamsArr(e.target.value);
      newLayers = getLayers(allParams);
      newParams = removeLayers(allParams);
    }

    setBase(newBase);
    setLayers(newLayers);
    setParams(newParams);
    setLayerSource(combineURL(newLayers, newParams, newBase));
  };

  const handleUpdateLayers = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLayers(e.target.value);
    setLayerSource(combineURL(e.target.value, params, base));
  };

  const handleUpdateParams = (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    let newParams = [...params];

    newParams.forEach((param, i: number) => {
      if (index === i) {
        const prop = Object.keys(param)[0];
        newParams.splice(index, 1, e.target.name === 'key' ? { [e.target.value]: param[prop] } : { [prop]: e.target.value });
      }
    });
    newParams = removeLayers(newParams);

    setParams(newParams);
    setLayerSource(combineURL(layers, newParams, base));
  };

  const renderParam = (param: Param, idx: number) => {
    const key = Object.keys(param)[0];

    return (
      <div key={idx}>
        <input
          type="text"
          name="key"
          style={{ marginRight: 10 }}
          value={key}
          placeholder="key"
          onChange={handleUpdateParams(idx)}
        />
        <input
          type="text"
          placeholder="value"
          value={param[key]}
          onChange={handleUpdateParams(idx)}
        />
      </div>
    );
  };

  return (
    <div data-testid="wms-layer" className="layer-type-form">
      <div className="layer-source clearfix">
        <label htmlFor="base_layer" className="required">Base Server URL</label>
        <div className="input">
          <input
            type="hidden"
            name="layer[source]"
            id="layer_source"
            value={layerSource}
          />
        </div>
        <div className="input">
          <input
            required
            type="text"
            name="base_layer"
            id="base_layer"
            value={base}
            onChange={handleUpdateBase}
            className="span6"
          />
        </div>
        <label htmlFor="layers_input" className="required">Layers</label>
        <div className="input">
          <input
            required
            type="text"
            name="layers"
            id="layers_input"
            value={layers}
            onChange={handleUpdateLayers}
            className="span6"
          />
        </div>
        <span className="helper">
          Layers should be comma
          {' '}
          <code>,</code>
          {' '}
          separated
        </span>
        <label style={{ marginTop: 10 }} htmlFor="layer_source">URL Parameters</label>
        { /* @ts-ignore */ }
        <div id="url_parameters" className={cx('input', styles.params)}>
          {params.map((param, idx) => (renderParam(param, idx)))}
        </div>
      </div>
    </div>
  );
};

export function getBaseUrl(base: string): string {
  if (!base) {
    return '';
  }

  try {
    const urlBase = new URL(base);
    return urlBase.origin + urlBase.pathname;
  } catch (err) {
    // invalid URL, it's okay because it's possible the URL field is empty
  }

  return base;
}

export function getParamsArr(base: string): Param[] {
  let params = [];

  if (!base) {
    return maybeAddEmptyRows(DEFAULT_WMS_PARAMS);
  }

  try {
    const url = new URL(base);
    const urlQueryParams = new URLSearchParams(url.search);

    urlQueryParams.forEach((value, key) => {
      // @ts-ignore
      params.push({ [key.toLowerCase()]: value });
    });
  } catch (err) {
    console.error(err);
  }
  // @ts-ignore
  params = maybeAddEmptyRows(params);
  return params;
}

export function createEmptyParams(count: number) {
  return [...Array(count)].map(() => ({ '': '' }));
}

function maybeAddEmptyRows(params: Param[]) {
  return [...params, ...createEmptyParams(20 - params.length)];
}

export function getLayers(params: Param[]): string {
  const param = params.filter((p) => p.layers)[0];
  return param ? param.layers : '';
}

export function hasParams(baseUrl: string) {
  const allParams = [];

  let url;
  try {
    url = new URL(baseUrl);
  } catch (error) {
    return false;
  }

  const urlQueryParams = new URLSearchParams(url.search);
  // @ts-ignore
  urlQueryParams.forEach((queryParam) => allParams.push(queryParam));

  return allParams.length > 0;
}

export function combineURL(layers: string, params: Param[], base: string): string {
  const urlParams = new URLSearchParams();

  if (params.length > 0) {
    params.forEach((urlParam) => {
      const urlkey = Object.keys(urlParam)[0];
      if (urlkey || urlParam[urlkey]) {
        urlParams.append(urlkey, urlParam[urlkey]);
      }
    });
  }

  if (layers) {
    urlParams.append('layers', layers);
  }

  return `${base}?${urlParams}` === '?' ? '' : `${base}?${urlParams}`;
}

function removeLayers(paramsArr: Param[]) {
  const removedLayers = paramsArr.filter((param) => Object.keys(param)[0] !== 'layers');
  return maybeAddEmptyRows(removedLayers);
}

const styles = css({
  params: {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 230,
    overflow: 'auto',
  },
});

export default WMSLayer;
